/**
 * Audio resolution: flattens a sequence (including nested sequences, transitions, track/clip
 * volumes and volume keyframes) into a list of independent playback segments. Each segment plays
 * one region of one media file with a gain envelope. Preview (AudioContext) and export
 * (OfflineAudioContext) schedule exactly the same segments.
 */
import { applyEasing } from '../animation/keyframes.ts';
import { PARAM_DEFINITIONS } from '../model/params.ts';
import { framesToSeconds } from '../model/time.ts';
import type { Clip, Id, Project, Sequence, Track } from '../model/schema.ts';
import { clipEnd, transitionRange } from '../timeline/queries.ts';
import {
  constantEnvelope,
  cropEnvelope,
  multiplyEnvelopes,
  rampEnvelope,
  scaleEnvelope,
  shiftEnvelope,
  type Envelope,
} from './envelope.ts';
import { MAX_NESTING_DEPTH } from './resolve-frame.ts';

export interface AudioSegment {
  key: string;
  mediaId: Id;
  /** Timeline start, seconds. */
  start: number;
  /** Timeline duration, seconds. */
  duration: number;
  /** Position in the media at `start`, seconds. */
  sourceStart: number;
  /** Playback rate (pitch is not preserved). */
  speed: number;
  /** Gain over time, in timeline seconds. */
  gain: Envelope;
}

const EASE_SUBDIVISIONS = 12;

/** Builds a clip's volume envelope in timeline seconds. */
function volumeEnvelope(project: Project, clip: Clip, clipStartSec: number): Envelope {
  const anim = clip.params.volume;
  const fps = project.settings.fps;
  if (!anim || anim.keyframes.length === 0) {
    return constantEnvelope(anim?.value ?? PARAM_DEFINITIONS.volume.defaultValue);
  }
  const times: number[] = [];
  const values: number[] = [];
  const kfs = anim.keyframes;
  kfs.forEach((a, i) => {
    const ta = clipStartSec + framesToSeconds(a.frame, fps);
    times.push(ta);
    values.push(a.value);
    const b = kfs[i + 1];
    if (!b || a.easing === 'linear') return;
    const tb = clipStartSec + framesToSeconds(b.frame, fps);
    if (a.easing === 'hold') {
      times.push(tb - 1e-4);
      values.push(a.value);
      return;
    }
    for (let s = 1; s < EASE_SUBDIVISIONS; s++) {
      const u = s / EASE_SUBDIVISIONS;
      times.push(ta + (tb - ta) * u);
      values.push(a.value + (b.value - a.value) * applyEasing(a.easing, u));
    }
  });
  return { times, values };
}

interface ClipWindow {
  /** Timeline seconds, including transition handles. */
  start: number;
  end: number;
  fades: Envelope[];
}

/** Extends a clip into neighbouring transitions and builds the crossfade envelopes. */
function clipWindow(project: Project, track: Track, clip: Clip): ClipWindow {
  const fps = project.settings.fps;
  let start = framesToSeconds(clip.start, fps);
  let end = framesToSeconds(clipEnd(clip), fps);
  const fades: Envelope[] = [];
  for (const tr of track.transitions) {
    const range = transitionRange(track, tr);
    if (!range) continue;
    const s = framesToSeconds(range.start, fps);
    const e = framesToSeconds(range.end, fps);
    if (tr.toClipId === clip.id) {
      start = Math.min(start, s);
      fades.push(rampEnvelope(s, 0, e, 1));
    }
    if (tr.fromClipId === clip.id) {
      end = Math.max(end, e);
      fades.push(rampEnvelope(s, 1, e, 0));
    }
  }
  return { start, end, fades };
}

function audibleTracks(seq: Sequence): Track[] {
  const tracks = [...seq.videoTracks, ...seq.audioTracks];
  const anySolo = tracks.some((t) => t.solo);
  return tracks.filter((t) => !t.muted && (!anySolo || t.solo));
}

function resolveSequenceAudio(
  project: Project,
  seq: Sequence,
  keyPrefix: string,
  depth: number,
): AudioSegment[] {
  const fps = project.settings.fps;
  const out: AudioSegment[] = [];
  for (const track of audibleTracks(seq)) {
    for (const clip of track.clips) {
      if (!clip.enabled) continue;
      if (clip.type !== 'video' && clip.type !== 'audio' && clip.type !== 'sequence') continue;
      if (clip.muted) continue;
      const clipStartSec = framesToSeconds(clip.start, fps);
      const win = clipWindow(project, track, clip);
      let gain = scaleEnvelope(volumeEnvelope(project, clip, clipStartSec), track.volume);
      for (const f of win.fades) gain = multiplyEnvelopes(gain, f);
      const key = keyPrefix + clip.id;

      if (clip.type === 'sequence') {
        const child = project.sequences[clip.sequenceId];
        if (!child || depth >= MAX_NESTING_DEPTH) continue;
        // child time c ↦ parent time c + offset
        const offset = clipStartSec - framesToSeconds(clip.sourceIn, fps);
        for (const seg of resolveSequenceAudio(project, child, key + '/', depth + 1)) {
          const segStart = Math.max(seg.start + offset, win.start);
          const segEnd = Math.min(seg.start + seg.duration + offset, win.end);
          if (segEnd - segStart <= 1e-6) continue;
          const shifted = shiftEnvelope(seg.gain, offset);
          out.push({
            key: seg.key,
            mediaId: seg.mediaId,
            start: segStart,
            duration: segEnd - segStart,
            sourceStart: seg.sourceStart + (segStart - (seg.start + offset)) * seg.speed,
            speed: seg.speed,
            gain: cropEnvelope(multiplyEnvelopes(shifted, gain), segStart, segEnd),
          });
        }
        continue;
      }

      const media = project.media[clip.mediaId];
      if (!media?.hasAudio) continue;
      // Media position at timeline time t: sourceIn + (t - clipStart) * speed.
      const srcAt = (t: number) => clip.sourceIn + (t - clipStartSec) * clip.speed;
      let start = win.start;
      let end = win.end;
      if (srcAt(start) < 0) start = clipStartSec - clip.sourceIn / clip.speed;
      if (media.duration !== undefined && srcAt(end) > media.duration) {
        end = clipStartSec + (media.duration - clip.sourceIn) / clip.speed;
      }
      if (end - start <= 1e-6) continue;
      out.push({
        key,
        mediaId: clip.mediaId,
        start,
        duration: end - start,
        sourceStart: srcAt(start),
        speed: clip.speed,
        gain: cropEnvelope(gain, start, end),
      });
    }
  }
  return out;
}

/** Resolves all audio of a sequence into flat segments sorted by start time. */
export function resolveAudio(project: Project, sequenceId: Id): AudioSegment[] {
  const seq = project.sequences[sequenceId];
  if (!seq) return [];
  return resolveSequenceAudio(project, seq, '', 0).sort((a, b) => a.start - b.start);
}
